import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { CalendarIcon, PaperplaneIcon, PersonGroupIcon } from '@navikt/aksel-icons';
import { formatScore, scoreLevel } from '../hrPsychologicalSafetyView';
import classes from '../HRPsychologicalSafety.module.css';

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

type SummaryCardProps = {
  averageScore: number;
  teamsWithResults: number;
  activeTeams: number;
  ongoingSurveys?: number;
  plannedSurveys?: number;
};

/** Snittet i organisasjonen som ring, med tolkning og nøkkeltall */
export const SummaryCard = ({
  averageScore,
  teamsWithResults,
  activeTeams,
  ongoingSurveys,
  plannedSurveys,
}: SummaryCardProps) => {
  const { t, i18n } = useTranslation();
  const hasResults = teamsWithResults > 0;
  const level = hasResults ? scoreLevel(averageScore) : null;
  const filled = hasResults ? (CIRCUMFERENCE * (averageScore - 1)) / 4 : 0;
  const facts = [
    { key: 'activeTeams', value: activeTeams, Icon: PersonGroupIcon },
    { key: 'ongoingSurveys', value: ongoingSurveys, Icon: PaperplaneIcon },
    { key: 'plannedSurveys', value: plannedSurveys, Icon: CalendarIcon },
  ];

  return (
    <section aria-labelledby="hr-ps-summary" className={classes.summary}>
      <div className={classes.summaryMain}>
        <div className={classes.ring}>
          <svg viewBox="0 0 120 120" aria-hidden="true">
            <circle cx="60" cy="60" r={RADIUS} className={classes.ringTrack} />
            {hasResults && (
              <circle
                cx="60"
                cy="60"
                r={RADIUS}
                className={classes.ringFill}
                data-level={level}
                strokeDasharray={`${filled} ${CIRCUMFERENCE}`}
              />
            )}
          </svg>
          <span className={classes.ringValue}>
            <span className={classes.ringScore}>{hasResults ? formatScore(averageScore, i18n.language) : '–'}</span>
            <span className={classes.ringOf}>{t('hrPsychologicalSafety.summary.of')}</span>
          </span>
        </div>
        <div className={classes.summaryText}>
          {level && (
            <div>
              <Tag data-size="sm" data-color={level}>
                {t(`hrPsychologicalSafety.summary.levels.${level}.tag`)}
              </Tag>
            </div>
          )}
          <Heading level={2} data-size="xs" id="hr-ps-summary">
            {t(`hrPsychologicalSafety.summary.levels.${level ?? 'none'}.title`)}
          </Heading>
          <Paragraph data-size="sm">
            {t(`hrPsychologicalSafety.summary.levels.${level ?? 'none'}.text`)}{' '}
            {hasResults &&
              teamsWithResults < activeTeams &&
              t('hrPsychologicalSafety.summary.coverage', { count: teamsWithResults, total: activeTeams })}
          </Paragraph>
        </div>
      </div>
      <dl className={classes.facts}>
        {facts.map(({ key, value, Icon }) => (
          <div key={key}>
            <dt>
              <Icon aria-hidden />
              {t(`hrPsychologicalSafety.summary.facts.${key}`)}
            </dt>
            <dd>{value ?? '–'}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
